import {
  localDayKey,
  type BookSectionsResponse,
  type ChallengeListResponse,
  type ChallengeResponse,
  type ConfigResponse,
  type GuessRequest,
  type GuessResponse,
  type HintRequest,
  type HintResponse,
  type BibleChapterResponse,
  type RevealResponse,
  type Round3Response,
  type SendRequest,
  type SendResponse,
  type SentPuzzleResponse,
  type SentStatsResponse,
  type UnredactedResponse,
} from "@versedrill/core";
import { useStore } from "./store.ts";
import { withVersion } from "./version.ts";

const BASE = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/$/, "") ?? "";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** The parsed error body, when the server sent one (a sync conflict carries the current copy here). */
    readonly details?: unknown,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, init);
  } catch {
    throw new ApiError(0, "NETWORK", "Can't reach the server. Check your connection and try again.");
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { code?: string; message?: string } } | null;
    // An edge rate-limit block is Cloudflare's own page, not our JSON, so it has no message of its own.
    const fallback = res.status === 429 ? "You're going a little fast. Wait a few seconds and try again." : `Request failed (${res.status})`;
    throw new ApiError(res.status, body?.error?.code ?? (res.status === 429 ? "RATE_LIMITED" : "ERROR"), body?.error?.message ?? fallback, body ?? undefined);
  }
  return (await res.json()) as T;
}

/** The player's own calendar date: games unlock at *their* midnight, so the server needs to know it. */
const today = () => localDayKey(new Date());
const post = (body: unknown): RequestInit => ({ method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
const dotted = (p: readonly number[]) => p.join(".");
/** The version the player is reading in, read when each request is made so a change applies to the very next fetch. */
const ver = (url: string) => withVersion(url, useStore.getState().translation);

// Static-ish data is cached for the life of the page.
interface Api {
  config: () => Promise<ConfigResponse>;
  list: () => Promise<ChallengeListResponse>;
  challenge: (n: number) => Promise<ChallengeResponse>;
  unredacted: (n: number) => Promise<UnredactedResponse>;
  guess: (n: number, body: GuessRequest) => Promise<GuessResponse>;
  hint: (n: number, body: HintRequest) => Promise<HintResponse>;
  bookSections: (id: number) => Promise<BookSectionsResponse>;
  round3: (n: number, bookPath: readonly number[], sectionPath: readonly number[]) => Promise<Round3Response>;
  reveal: (n: number, bookPath: readonly number[], sectionPath: readonly number[], slot?: number) => Promise<RevealResponse>;
}
let configCache: Promise<ConfigResponse> | undefined;
const sectionCache = new Map<number, Promise<BookSectionsResponse>>();

export const api: Api = {
  config: () => (configCache ??= request<ConfigResponse>("/api/config").catch((e) => ((configCache = undefined), Promise.reject(e)))),
  list: () => request<ChallengeListResponse>(`/api/challenges?date=${today()}`),
  challenge: (n: number) => request<ChallengeResponse>(ver(`/api/challenges/${n}?date=${today()}`)),
  unredacted: (n: number) => request<UnredactedResponse>(ver(`/api/challenges/${n}/unredacted?date=${today()}`)),
  guess: (n: number, body: GuessRequest) => request<GuessResponse>(`/api/challenges/${n}/guess?date=${today()}`, post(body)),
  hint: (n: number, body: HintRequest) => request<HintResponse>(`/api/challenges/${n}/hint?date=${today()}`, post(body)),
  bookSections: async (id: number) => {
    // The config's sectionsVersion busts caches whenever labels change (editing, or the daily job filling gaps).
    const { sectionsVersion } = await api.config();
    const key = id * 1_000_000 + sectionsVersion;
    let p = sectionCache.get(key);
    if (!p) {
      p = request<BookSectionsResponse>(`/api/books/${id}/sections?v=${sectionsVersion}`);
      sectionCache.set(key, p);
      p.catch(() => sectionCache.delete(key));
    }
    return p;
  },
  round3: (n: number, bookPath: readonly number[], sectionPath: readonly number[]) =>
    request<Round3Response>(ver(`/api/challenges/${n}/round3?date=${today()}&bookPath=${dotted(bookPath)}&sectionPath=${dotted(sectionPath)}`)),
  reveal: (n: number, bookPath: readonly number[], sectionPath: readonly number[], slot?: number) =>
    request<RevealResponse>(
      ver(`/api/challenges/${n}/reveal?date=${today()}&bookPath=${dotted(bookPath)}&sectionPath=${dotted(sectionPath)}${slot === undefined ? "" : `&slot=${slot}`}`),
    ),
};

// ---- Daily reminders ---------------------------------------------------------------------------------------------

const put = (body: unknown): RequestInit => ({ method: "PUT", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
export const pushApi = {
  /** Sign up, or check in (the app does this at every launch so the server has the time zone, hour and last-played date). */
  subscribe: (body: { endpoint: string; tz: string; hour: number; lastPlayed: string | null }) => request<{ ok: true }>("/api/push/subscription", put(body)),
  unsubscribe: (endpoint: string) => request<{ ok: true }>("/api/push/subscription", { method: "DELETE", headers: { "content-type": "application/json" }, body: JSON.stringify({ endpoint }) }),
  test: (endpoint: string) => request<{ ok: true }>("/api/push/test", post({ endpoint })),
};

// ---- Cross-device sync (the server stores ciphertext it cannot read; see lib/sync/) ------------------------------

export interface SyncTransport {
  /** The stored copy, or null if there is none under this id. */
  get: (id: string, auth: string) => Promise<{ body: string; version: number } | null>;
  /** Writes if `version` is still current; otherwise resolves to the current copy so the caller can merge and retry. */
  put: (id: string, auth: string, body: string, version: number) => Promise<{ ok: true; version: number } | { ok: false; current: { body: string; version: number } | null }>;
  del: (id: string, auth: string) => Promise<void>;
}
const syncHeaders = (auth: string) => ({ "content-type": "application/json", "X-Sync-Auth": auth });
export const syncApi: SyncTransport = {
  get: async (id, auth) => {
    try {
      return await request<{ body: string; version: number }>(`/api/sync/${id}`, { headers: syncHeaders(auth) });
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return null;
      throw e;
    }
  },
  put: async (id, auth, body, version) => {
    try {
      return { ok: true, ...(await request<{ version: number }>(`/api/sync/${id}`, { method: "PUT", headers: syncHeaders(auth), body: JSON.stringify({ body, version }) })) };
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) return { ok: false, current: (e.details as { current?: { body: string; version: number } | null })?.current ?? null };
      throw e;
    }
  },
  del: async (id, auth) => void (await request<{ ok: true }>(`/api/sync/${id}`, { method: "DELETE", headers: syncHeaders(auth) })),
};

// ---- Send a Verse ---------------------------------------------------------------------------------------------

export const sentApi = {
  create: (body: SendRequest) => request<SendResponse>("/api/sent", post(body)),
  stats: (code: string) => request<SentStatsResponse>(`/api/sent/${code}/stats`),
  solved: (code: string, playerId: string, stars: number) => request<{ ok: true }>(`/api/sent/${code}/solved`, post({ playerId, stars })),
  remove: (code: string, manageToken: string) => request<{ ok: true }>(`/api/sent/${code}`, { method: "DELETE", headers: { "X-Manage-Token": manageToken } }),
  report: (code: string, playerId: string) => request<{ ok: true }>(`/api/sent/${code}/report`, post({ playerId })),
  chapter: (bookId: number, chapter: number) => request<BibleChapterResponse>(ver(`/api/bible/${bookId}/${chapter}`)),
};

// ---- What a game is played against -----------------------------------------------------------------------------

/** Everything the game hook needs from the server. A daily game and a sent verse play identically through this. */
export interface GameSource {
  /** Stable identity, so the hook resets when the game changes. */
  key: string;
  challenge: () => Promise<SentPuzzleResponse | (ChallengeResponse & { from?: undefined; code?: undefined })>;
  unredacted: () => Promise<UnredactedResponse>;
  guess: (body: GuessRequest) => Promise<GuessResponse>;
  hint: (body: HintRequest) => Promise<HintResponse>;
  round3: (bookPath: readonly number[], sectionPath: readonly number[]) => Promise<Round3Response>;
  reveal: (bookPath: readonly number[], sectionPath: readonly number[], slot?: number) => Promise<RevealResponse>;
}

export const dailySource = (n: number): GameSource => ({
  key: `daily:${n}`,
  challenge: () => api.challenge(n),
  unredacted: () => api.unredacted(n),
  guess: (b) => api.guess(n, b),
  hint: (b) => api.hint(n, b),
  round3: (bp, sp) => api.round3(n, bp, sp),
  reveal: (bp, sp, slot) => api.reveal(n, bp, sp, slot),
});

export const sentSource = (code: string): GameSource => ({
  key: `sent:${code}`,
  challenge: () => request<SentPuzzleResponse>(ver(`/api/sent/${code}`)),
  unredacted: () => request<UnredactedResponse>(ver(`/api/sent/${code}/unredacted`)),
  guess: (b) => request<GuessResponse>(`/api/sent/${code}/guess`, post(b)),
  hint: (b) => request<HintResponse>(`/api/sent/${code}/hint`, post(b)),
  round3: (bp, sp) => request<Round3Response>(ver(`/api/sent/${code}/round3?bookPath=${dotted(bp)}&sectionPath=${dotted(sp)}`)),
  reveal: (bp, sp, slot) =>
    request<RevealResponse>(ver(`/api/sent/${code}/reveal?bookPath=${dotted(bp)}&sectionPath=${dotted(sp)}${slot === undefined ? "" : `&slot=${slot}`}`)),
});
